import { identity } from 'lodash';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Select } from '~/eds/src/components/Select/Select';
import { useThrottledState } from '~/eds/src/hooks/useThrottledState';
import { Option } from '~/eds/src/types/Option';
import { UserAction } from '~/eds/src/types/UserAction';
import { AribaFieldSubType } from '~/enums/AribaFieldSubType';
import { DataFieldType } from '~/enums/DataFieldType';
import { getIconFromFieldType } from '~/features/fields/utils';
import { api } from '~/redux/api/createApi';
import { DataField } from '~/types/Field';
import { Nullable } from '~/types/Nullable';
import { PilotId } from '~/types/PilotId';
import { sortByStringValue } from '~/utils/array';

type Props = {
  value: Nullable<PilotId>;
  onChange: (field: Nullable<DataField>) => void;
  disabled?: boolean;
  aribaFieldType: AribaFieldSubType;
  footerAction?: UserAction;
  filterOption?: (option: Option<PilotId>) => boolean;
  isLoading?: boolean;
  name?: string;
  mapOption?: (
    option: Option<PilotId, DataField>,
  ) => Option<PilotId, DataField>;
  sortOption?: (
    a: Option<PilotId, DataField>,
    b: Option<PilotId, DataField>,
  ) => number;
};

export const FieldMappingSelect = ({
  disabled,
  filterOption,
  footerAction,
  isLoading: overrideIsLoading,
  name = 'field-select',
  mapOption = identity,
  onChange,
  sortOption = sortByStringValue('label'),
  value,
}: Props) => {
  const intl = useIntl();
  const [throttledSearch, _search, setSearch] = useThrottledState('');
  const {
    data: searchableFields,
    error: searchableFieldsError,
    isFetching: isFetchingSearchableFields,
  } = api.endpoints.getFields.useQuery({
    limit: 100,
    search: throttledSearch,
  });

  const {
    data: resolvedFields,
    error: resolvedFieldsError,
    isFetching: isFetchingResolvedFields,
  } = api.endpoints.getFields.useQuery(
    {
      limit: 1,
      ids: value ? [value] : undefined,
    },
    {
      skip: !value,
    },
  );

  const error =
    resolvedFieldsError || searchableFieldsError
      ? intl.formatMessage({
          id: 'FieldMappingSelect.unableToLoadFields',
          defaultMessage: 'Unable to load fields',
          description: 'Error message when fields cannot be loaded',
        })
      : undefined;
  const isLoading =
    overrideIsLoading || isFetchingSearchableFields || isFetchingResolvedFields;
  const syntheticFields = [
    {
      id: -1,
      label: intl.formatMessage({
        id: 'FieldMappingSelect.documentUrl',
        defaultMessage: 'Document URL',
        description: 'Label for synthetic document URL field',
      }),
      type: DataFieldType.STRING,
      helpText: intl.formatMessage({
        id: 'FieldMappingSelect.documentUrlHelpText',
        defaultMessage: 'The URL of the document in Evisort',
        description: 'Help text for document URL field',
      }),
      isSmart: true,
    } as DataField,
  ];

  const options = useMemo(() => {
    const fields = [
      ...(resolvedFields?.results ?? []),
      ...(searchableFields?.results ?? []),
      ...syntheticFields,
    ];
    // the field ariba messages was added to show errors, so should not be mapped
    const excludedFields = ['ariba messages', 'ariba primary document'];
    return fields
      .map((field) => ({
        data: field,
        label: field.label,
        leftIcon: getIconFromFieldType(field.type),
        value: field.id,
      }))
      .map(mapOption)
      .filter(
        (field) => !excludedFields.includes(field.label.toLocaleLowerCase()),
      )
      .sort(sortOption);
  }, [resolvedFields, searchableFields, mapOption, sortOption]);

  const optionsMap: Record<PilotId, DataField> = useMemo(
    () =>
      Object.fromEntries(options.map((option) => [option.value, option.data!])),
    [options],
  );

  const handleChange = (value: Nullable<PilotId>) => {
    onChange(value === null ? null : optionsMap[value]);
  };

  return (
    <Select
      disabled={disabled}
      error={error}
      filterOption={filterOption}
      footerAction={footerAction}
      isClearable={false}
      isLoading={isLoading}
      isMulti={false}
      name={name}
      options={options}
      placeholder={intl.formatMessage({
        id: 'FieldMappingSelect.searchPlaceholder',
        defaultMessage: 'Search for a field…',
        description: 'Placeholder text for field search input',
      })}
      value={value}
      onChange={handleChange}
      onSearch={setSearch}
    />
  );
};
